// aiCapabilities.js — "WHAT CAN YOU DO HERE?", answered from what is
// installed, never from a guess (DESIGN-COMPOSER-FOCUS-POPOVER-CONTEXT-
// INSPECT-1, Nika's addition 2026-10-04: "improve 'რისი გაკეთება შეიძლება'
// section make it more clear").
//
// The answer was the model's free prose ("what can you do here?" → a vague
// list). Now it is a CATALOGUE for the chosen context (functions/aiScope.js),
// built from the installed apps' own manifests and the shell's own abilities,
// in four groups — each item tappable:
//
//   Ask      2–3 real questions for this context (the app's own starters, a
//            record's own actions) — a tap asks it
//   Do       the app's approval-gated actions (mint tokens, move funds,
//            acknowledge an incident) — a tap asks the assistant to prepare
//            it; nothing runs before the member approves the proposal card
//   Build    a board for this context, and the app's widgets the board can
//            hold — a tap builds (the Dashboard path, for that ask only)
//   Inspect  point at a widget or a section and ask about it — a tap starts it
//
// `aiShowCapabilities()` answers the panel's "What can you do here?" row AT
// ONCE, with no model call: the member's line and the catalogue land in the
// thread where answers land (the main thread, or the assistant sidebar).
// `aiCapabilities(root)` is the pure catalogue; `aiCapabilityRun(item)` runs
// one row.

import { shellSay } from './shellSharedDelegates.js'
import { aiRecordSuggestionItems } from './pageContext.js'
import { aiScopeCurrent, aiScopeSurface, aiInstalledApps } from './aiScope.js'

const _AIC_ASK_MAX = 3
const _AIC_DO_MAX = 4
const _AIC_WIDGETS_MAX = 3
// a catalogue line's description, in characters (one or two lines in the card)
const _AIC_LINE_MAX = 120

const _aicStr = (v) => (v == null ? '' : String(v))
const _aicText = (el, key) => {
  const t = _aicStr(key)
  if (!t) return ''
  try {
    return el && typeof el.call === 'function' ? _aicStr(el.call('polyglot', t)) || t : t
  } catch (_) {
    return t
  }
}
// A sentence with ONE named hole, filled by a FUNCTION (a `$&` in a tenant's
// name must never read as a replacement pattern). Each caller names its own
// placeholder literally, so the bundle and the source agree on it.
const _aicFill = (el, key, token, value) => shellSay(el, key).replace(token, () => _aicStr(value))
// "mintTokens" → "Mint tokens": an action that declares no label still reads
// as words, never as its key.
const _aicWords = (key) => {
  const words = _aicStr(key)
    .replace(/([a-z0-9])([A-Z])/g, '$1 $2')
    .replace(/[-_]+/g, ' ')
    .trim()
    .toLowerCase()
  return words ? words[0].toUpperCase() + words.slice(1) : ''
}
// A line for a PERSON from a module's `describe` (written for the assistant —
// long, with parameter notes): its first sentence, cut at a word with an
// ellipsis when it is still long.
const _aicSentence = (v, max) => {
  const t = _aicStr(v).replace(/\s+/g, ' ').trim()
  if (!t) return ''
  const end = t.search(/[.!?](\s|$)/)
  const first = end > 0 ? t.slice(0, end + 1) : t
  if (first.length <= max) return first
  const cut = first.slice(0, max)
  const space = cut.lastIndexOf(' ')
  return (space > max * 0.6 ? cut.slice(0, space) : cut).replace(/[\s,;:·(-]+$/, '') + '…'
}
const _aicKeyItems = (grp) => {
  const items = []
  grp.items.forEach((it, i) => items.push({ ...it, key: grp.key + '-' + i }))
  return { ...grp, items }
}
const _aicStarterText = (el, it) => {
  const raw = typeof it === 'string' ? it : it && typeof it.text === 'string' ? it.text : ''
  return _aicText(el, raw.trim())
}

// THE CATALOGUE for the chosen context.
export const aiCapabilities = function aiCapabilities(root) {
  const el = this
  const r = root && typeof root === 'object' ? root : {}
  const row = aiScopeCurrent.call(el, r)
  if (!row) return null
  const sf = aiScopeSurface.call(el, r)
  const apps = aiInstalledApps(r)
  const inScope =
    row.kind === 'workspace' ? apps : row.appId ? apps.filter((a) => a.id === row.appId) : []

  // ASK — the context's own questions.
  const ask = []
  if (row.kind === 'record' && sf.record) {
    for (const s of aiRecordSuggestionItems(sf.record.type).slice(0, _AIC_ASK_MAX)) {
      ask.push({ kind: 'ask', label: _aicText(el, s.labelKey), prompt: _aicText(el, s.promptKey) })
    }
  } else if (row.kind === 'workspace') {
    const merged = Array.isArray(r.workspaceProject && r.workspaceProject.chatStarters)
      ? r.workspaceProject.chatStarters
      : []
    for (const it of merged.slice(0, _AIC_ASK_MAX)) {
      const t = _aicStarterText(el, it)
      if (t) ask.push({ kind: 'ask', label: t, prompt: t })
    }
  } else {
    for (const app of inScope) {
      for (const it of app.chatStarters) {
        if (ask.length >= _AIC_ASK_MAX) break
        const t = _aicStarterText(el, it)
        if (t) ask.push({ kind: 'ask', label: t, prompt: t })
      }
    }
  }
  if (!ask.length) {
    const t = shellSay(el, 'workspace.aiCap.askFallback')
    ask.push({
      kind: 'ask',
      label: t,
      prompt: _aicFill(el, 'workspace.aiCap.askFallbackPrompt', '{name}', row.label)
    })
  }

  // DO — the approval-gated actions the apps in this context declare.
  const doItems = []
  for (const app of inScope) {
    for (const key of app.actionKeys) {
      if (doItems.length >= _AIC_DO_MAX) break
      const label = _aicText(el, app.actionLabels[key]) || _aicWords(key)
      const describe = _aicSentence(app.actionDocs[key], _AIC_LINE_MAX)
      doItems.push({
        kind: 'do',
        label,
        describe,
        action: key,
        app: app.id,
        prompt: _aicFill(el, 'workspace.aiCap.doPrompt', '{action}', label)
      })
    }
  }

  // BUILD — a board for the context, and the widgets it can hold.
  const build = [
    {
      kind: 'build',
      label: _aicFill(el, 'workspace.aiCap.buildBoard', '{name}', row.label),
      prompt: _aicFill(el, 'workspace.aiCap.buildBoardPrompt', '{name}', row.label)
    }
  ]
  const seenWidget = new Set()
  for (const app of inScope) {
    let perApp = 0
    for (const w of app.widgets) {
      if (build.length > _AIC_WIDGETS_MAX) break
      if (!w || !w.id || seenWidget.has(w.id) || w.kind === 'app') continue
      if (w.spec && w.spec.iconOnly) continue
      if (row.kind === 'workspace' && perApp >= 1) break
      const title = _aicText(el, w.title)
      if (!title) continue
      seenWidget.add(w.id)
      perApp++
      build.push({
        kind: 'build',
        label: title,
        // the widget's own subtitle (a bundle key it ships) reads for people;
        // its `describe` is written for the assistant
        describe: _aicText(el, w.subtitle) || _aicSentence(w.describe, _AIC_LINE_MAX),
        widget: w.id,
        app: app.id,
        prompt: _aicFill(el, 'workspace.aiCap.buildWidgetPrompt', '{widget}', title)
      })
    }
  }

  const groups = [
    {
      key: 'ask',
      title: shellSay(el, 'workspace.aiCap.ask'),
      explain: shellSay(el, 'workspace.aiCap.askExplain'),
      glyph: 'lnAssistant',
      items: ask
    }
  ]
  if (doItems.length) {
    groups.push({
      key: 'do',
      title: shellSay(el, 'workspace.aiCap.do'),
      explain: shellSay(el, 'workspace.aiCap.doExplain'),
      glyph: 'lnCheck',
      items: doItems
    })
  }
  groups.push({
    key: 'build',
    title: shellSay(el, 'workspace.aiCap.build'),
    explain: shellSay(el, 'workspace.aiCap.buildExplain'),
    glyph: 'lnDashboard',
    items: build
  })
  groups.push({
    key: 'inspect',
    title: shellSay(el, 'workspace.aiCap.inspect'),
    explain: shellSay(el, 'workspace.aiCap.inspectExplain'),
    glyph: 'lnInspect',
    items: [{ kind: 'inspect', label: shellSay(el, 'workspace.aiCap.inspectStart') }]
  })
  // Every line keyed by its group and place (the card's children keys).
  const keyed = groups.map(_aicKeyItems)
  return {
    scope: { kind: row.kind, label: row.label, ...(row.appId ? { app: row.appId } : {}) },
    title: _aicFill(el, 'workspace.aiCap.title', '{name}', row.label),
    groups: keyed
  }
}

// ANSWER "what can you do here?" at once: the member's line and the catalogue
// land where answers land — the main thread when it takes the ask, else the
// assistant sidebar — with no model call.
export const aiShowCapabilities = function aiShowCapabilities() {
  const el = this
  const root = el && el.state && el.state.root
  if (!root || typeof root.update !== 'function') return false
  const cat = aiCapabilities.call(el, root)
  if (!cat) return false
  const route =
    typeof el.context?.functions?.assistantSendRoute === 'function'
      ? el.call('assistantSendRoute', root, 'assistant')
      : null
  try {
    if (route === 'thread') el.call('assistantMainThreadShow')
    else root.update({ assistantOpen: true, _homeThreadOpen: false })
  } catch (_) {
    /* no thread view on this surface — the sidebar takes it */
  }
  const now = Date.now()
  const ask = shellSay(el, 'workspace.aiSuggest.generic.whatCanYouDo')
  const user = { id: 'u-cap-' + now, role: 'user', text: ask, ts: now }
  const answer = {
    id: 'a-cap-' + now,
    role: 'assistant',
    text: _aicFill(el, 'workspace.aiCap.intro', '{name}', cat.scope.label),
    ts: now + 1,
    streaming: false,
    cards: [{ type: 'capabilities', title: cat.title, scope: cat.scope, groups: cat.groups }],
    sources: [],
    confidence: null,
    level: null,
    metadata: { kind: 'capabilities' }
  }
  const msgs = Array.isArray(root.chatMessages) ? root.chatMessages : []
  root.update({ chatMessages: msgs.concat(user, answer) })
  try {
    el.call('persistActiveSession', 'general')
  } catch (_) {
    /* the local buffer holds it either way */
  }
  return true
}

// RUN one row of the catalogue (the card's buttons).
export const aiCapabilityRun = function aiCapabilityRun(item) {
  const el = this
  const x = item && typeof item === 'object' ? item : null
  if (!x) return false
  if (x.kind === 'inspect') return el.call('aiInspectStart')
  const text = _aicStr(x.prompt || x.label).trim()
  if (!text) return false
  const mode = x.kind === 'build' ? 'dashboard' : x.kind === 'do' ? 'assistant' : null
  el.call('homeAiSend', text, null, 0, null, mode ? { mode } : null)
  return true
}
